import React, { useState, useEffect } from 'react';
import config from '../../config';
import { AppBar, Container, Toolbar, Box, Button, IconButton, Menu, MenuItem, useMediaQuery, useTheme } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import MenuIcon from '@mui/icons-material/Menu';

const LOGO_IMAGE = "https://dp9h3awfnhccl.cloudfront.net/public_folder/logo-abhi-1-Logo.png";

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  background: 'linear-gradient(135deg, rgba(10,10,10,0.95) 0%, rgba(26,26,26,0.95) 50%, rgba(42,42,42,0.95) 100%)',
  backdropFilter: 'blur(20px)',
  boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.appBar,
  padding: theme.spacing(1, 0),
  border: '1px solid rgba(255,255,255,0.1)',
  borderTop: 'none'
}));

const NavButton = styled(Button)(({ theme, active }) => ({
  color: active ? '#FF6B35' : 'rgba(255,255,255,0.8)',
  marginLeft: theme.spacing(1.5),
  padding: theme.spacing(0.8, 1.5),
  borderRadius: '12px',
  fontWeight: 500,
  fontSize: '0.9rem',
  transition: 'all 0.3s ease',
  background: active ? 'rgba(255,107,53,0.1)' : 'transparent',
  border: active ? '1px solid rgba(255,107,53,0.3)' : '1px solid transparent',
  whiteSpace: 'nowrap',
  '&:hover': {
    color: '#FF6B35',
    backgroundColor: 'rgba(255,107,53,0.1)',
    border: '1px solid rgba(255,107,53,0.3)',
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(255,107,53,0.2)',
  },
  position: 'relative',
  '&::after': active ? {
    content: '""',
    position: 'absolute',
    bottom: '-2px',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '60%',
    height: '2px',
    background: 'linear-gradient(45deg, #FF6B35, #FF8A65)',
    borderRadius: '2px'
  } : {}
}));

const GetStartedButton = styled(Button)(({ theme }) => ({
  background: 'linear-gradient(45deg, #FF6B35 30%, #FF8A65 90%)',
  color: '#fff',
  marginLeft: theme.spacing(1.5),
  padding: theme.spacing(1, 2.5),
  borderRadius: '30px',
  fontWeight: 600,
  fontSize: '0.85rem',
  whiteSpace: 'nowrap',
  boxShadow: '0 6px 20px rgba(255,107,53,0.3)',
  transition: 'all 0.4s ease',
  border: '1px solid rgba(255,107,53,0.3)',
  '&:hover': {
    background: 'linear-gradient(45deg, #FF5722 30%, #FF6B35 90%)',
    transform: 'translateY(-2px) scale(1.02)',
    boxShadow: '0 8px 25px rgba(255,107,53,0.4)',
  }
}));

const NavBar = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('lg'));
  const [anchorEl, setAnchorEl] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [showAnalytics, setShowAnalytics] = useState(false);
  
  useEffect(() => {
    setShowAnalytics(config.features.showAnalytics);
  }, []);

  const handleMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleNavigate = (path) => {
    navigate(path);
    handleMenuClose();
  };

  const handleSubscribeClick = () => {
    handleNavigate('/pricing');
    handleMenuClose();
  };

  const isActive = (path) => location.pathname === path;

  return (
    <StyledAppBar>
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: isMobile ? 'center' : 'flex-start',
              cursor: 'pointer',
              flexShrink: 0,
              mr: 2
            }}
            onClick={() => handleNavigate('/')}
          >
            <img 
              src={LOGO_IMAGE} 
              alt="YoungPresso Logo" 
              style={{ 
                height: isMobile ? '60px' : '40px',
                width: isMobile ? '100%' : 'auto',
                maxWidth: isMobile ? '100%' : '300px',
                objectFit: 'contain',
                cursor: 'pointer'
              }} 
            />
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            {isMobile ? (
              <>
                <IconButton
                  edge="end"
                  aria-label="menu"
                  onClick={handleMenuOpen}
                  sx={{
                    color: 'rgba(255,255,255,0.8)',
                    background: 'rgba(255,255,255,0.1)',
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: '12px',
                    transition: 'all 0.3s ease',
                    '&:hover': {
                      color: '#FF6B35',
                      background: 'rgba(255,107,53,0.1)',
                      border: '1px solid rgba(255,107,53,0.3)',
                      transform: 'scale(1.05)',
                    }
                  }}
                >
                  <MenuIcon />
                </IconButton>
              <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
                PaperProps={{
                  sx: {
                    mt: 1.5,
                    background: 'linear-gradient(135deg, rgba(26,26,26,0.95) 0%, rgba(42,42,42,0.95) 100%)',
                    backdropFilter: 'blur(20px)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '16px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
                    '& .MuiMenuItem-root': {
                      color: 'rgba(255,255,255,0.8)',
                      padding: '12px 20px',
                      borderRadius: '8px',
                      margin: '4px 8px',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        color: '#FF6B35',
                        backgroundColor: 'rgba(255,107,53,0.1)',
                        border: '1px solid rgba(255,107,53,0.2)',
                        transform: 'translateX(4px)'
                      }
                    }
                  }
                }}
              >
                <MenuItem 
                  onClick={() => handleNavigate('/pricing')}
                  sx={{ color: isActive('/pricing') ? '#FF6B35' : 'inherit' }}
                >
                  Pricing and Plans
                </MenuItem>
                <MenuItem 
                  onClick={() => handleNavigate('/about')}
                  sx={{ color: isActive('/about') ? '#FF6B35' : 'inherit' }}
                >
                  About Us
                </MenuItem>
                <MenuItem 
                  onClick={() => handleNavigate('/contact')}
                  sx={{ color: isActive('/contact') ? '#FF6B35' : 'inherit' }}
                >
                  Contact
                </MenuItem>
                <MenuItem 
                  onClick={() => handleNavigate('/schools')}
                  sx={{ color: isActive('/schools') ? '#FF6B35' : 'inherit' }}
                >
                  For Schools
                </MenuItem>
                <MenuItem 
                  onClick={() => handleNavigate('/user-login')}
                  sx={{ color: isActive('/user-login') ? '#FF6B35' : 'inherit' }}
                >
                  Subscriber Zone
                </MenuItem>
                {config.features.showAnalytics && (
                  <MenuItem 
                    onClick={() => handleNavigate('/super-duper')}
                    sx={{ color: isActive('/super-duper') ? '#FF6B35' : 'inherit' }}
                  >
                    Analytics
                  </MenuItem>
                )}
                <MenuItem onClick={handleSubscribeClick}>Subscribe</MenuItem>
              </Menu>
              </>
            ) : (
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'nowrap' }}>
              <NavButton 
                onClick={() => handleNavigate('/pricing')}
                active={isActive('/pricing')}
              >
                Pricing and Plans
              </NavButton>
              <NavButton 
                onClick={() => handleNavigate('/about')}
                active={isActive('/about')}
              >
                About Us
              </NavButton>
              <NavButton 
                onClick={() => handleNavigate('/contact')}
                active={isActive('/contact')}
              >
                Contact
              </NavButton>
              <NavButton 
                onClick={() => handleNavigate('/schools')}
                active={isActive('/schools')}
              >
                For Schools
              </NavButton>
              <NavButton 
                onClick={() => handleNavigate('/user-login')}
                active={isActive('/user-login')}
              >
                Subscriber Zone
              </NavButton>
              {config.features.showAnalytics && (
                <NavButton 
                  onClick={() => handleNavigate('/super-duper')}
                  active={isActive('/super-duper')}
                >
                  Analytics
                </NavButton>
              )}
              <GetStartedButton
                variant="contained"
                onClick={handleSubscribeClick}
              >
                Subscribe Now
              </GetStartedButton>
              </Box>
            )}
          </Box>
        </Toolbar>
      </Container>
    </StyledAppBar>
  );
};

export default NavBar;
